import { Image, Text, View } from '@react-pdf/renderer';
import { parseDocument } from 'htmlparser2';

export function htmlToPdfElements(html: string): React.ReactNode[] {
    const cleanHtml = html.trim().replace(/^"|"$/g, '');

    const dom = parseDocument(cleanHtml).children;

    const renderNode = (node: any, key: string): React.ReactNode => {
        if (node.type === 'text') {
            return <Text key={key}>{node.data}</Text>;
        }

        if (node.type === 'tag') {
            const children = (node.children || []).map((child: any, i: number) =>
                renderNode(child, `${key}-${i}`)
            );

            switch (node.name) {
                case 'p':
                    return <Text key={key} style={{ fontSize: 10, marginBottom: 6 }}>{children}</Text>;
                case 'br':
                    return <Text key={key}>{'\n'}</Text>;
                case 'strong':
                case 'b':
                    return <Text key={key} style={{ fontWeight: 700 }}>{children}</Text>;
                case 'em':
                case 'i':
                    return <Text key={key} style={{ fontStyle: 'italic' }}>{children}</Text>;
                case 'u':
                    return <Text key={key} style={{ textDecoration: 'underline' }}>{children}</Text>;
                case 's':
                case 'del':
                    return <Text key={key} style={{ textDecoration: 'line-through' }}>{children}</Text>;
                case 'h1':
                    return <Text key={key} style={{ fontSize: 18, fontWeight: 700, marginBottom: 10 }}>{children}</Text>;
                case 'h2':
                    return <Text key={key} style={{ fontSize: 15, fontWeight: 700, marginBottom: 8 }}>{children}</Text>;
                case 'h3':
                    return <Text key={key} style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>{children}</Text>;
                case 'ul':
                    return <View key={key} style={{ marginLeft: 10 }}>{children}</View>;
                case 'li':
                    return <Text key={key} style={{ fontSize: 10, marginBottom: 4 }}>• {children}</Text>;
                case 'a':
                    return (
                        <Text key={key} style={{ color: 'blue', textDecoration: 'underline' }}>
                            {node.attribs?.href || ''} {children}
                        </Text>
                    );
                case 'img':
                    return (
                        <Image
                            key={key}
                            src={node.attribs?.src}
                            style={{ width: 200, height: 'auto', marginBottom: 8 }}
                        />
                    );
                case 'span':
                    return <Text key={key}>{children}</Text>;
                case 'div':
                    return <View key={key}>{children}</View>;
                default:
                    return <Text key={key}>{children}</Text>;
            }
        }

        return null;
    };

    return dom.map((node, i) => renderNode(node, `pdf-${i}`));
}

export const CRITICAL_NOTAM_RULES = [
    { id: 'ad_closed', category: 'Infrastructure', regex: /\b(AD|AERODROME|AIRPORT|AIRFIELD)\s+(CLSD|CLOSED|CLOSURE)\b/i },
    { id: 'rwy_closed', category: 'Infrastructure', regex: /\b(RWY|RUNWAY)\s+(\d{2}[LCR]?|\d{2})\s+(CLSD|CLOSED|CLOSURE)\b/i },
    { id: 'fire_cat', category: 'Safety', regex: /\b(ARFF|FIRE\s+CAT|FIRE\s+CATEGORY|RFFS)\b.*\b(NOT\s+AVBL|NOT\s+AVAILABLE|DOWNGRADED|NOT\s+PROVIDED)\b/i },
    { id: 'atc_down', category: 'Air Traffic Control', regex: /\b(TWR|TOWER|APP|APPROACH|ATC|RADAR)\s+(NOT\s+AVBL|NOT\s+AVAILABLE|CLSD|CLOSED|SUSPENDED)\b/i },
    { id: 'braking_poor', category: 'Runway Condition', regex: /\b(BRAKING\s+ACTION|FRICTION)\b.*\b(POOR|UNRELIABLE|NIL|MEDIUM\s+TO\s+POOR)\b/i },
    { id: 'prohibited_area', category: 'Airspace', regex: /\b(TEMPORARY\s+PROHIBITED\s+AREA|PROHIBITED\s+AREA|RESTRICTED\s+AREA|DANGER\s+AREA)\b/i },
    { id: 'navaid_out', category: 'Navigation', regex: /\b(ILS|VOR|GLIDESLOPE|GP|LOC|LOCALIZER)\b.*\b(U\/S|UNSERVICEABLE|OUT\s+OF\s+SERVICE|NOT\s+AVBL)\b/i },
    { id: 'security', category: 'Security', regex: /\b(SECURITY\s+THREAT|EVACUATION|BOMB|TERROR|ATTACK)\b/i }
];